import React, { Fragment, useMemo } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';

import Heading from '@atlaskit/heading';
import { useIntl } from '@atlassian/jira-intl';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { Activity, ActivityType } from '../../../common/types.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import { EmptyState } from '../../../common/ui/empty-state/index.tsx';
import { ActivityItem } from './activity-item/index.tsx';
import messages from './messages.tsx';

const emptyIcon = (
	<svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M79.8599 43.4902H31.1499V67.2902H79.8599V43.4902Z" fill="#1868DB" />
		<path d="M64.72 28.71H16.01V52.51H64.72V28.71Z" fill="#DDDEE1" />
		<path d="M64.7199 43.4902H31.1499V52.5102H64.7199V43.4902Z" fill="#09326C" />
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M76.8299 52.09L66.3399 62.58L60.9199 57.01L63.2599 54.74L66.3799 57.94L74.5299 49.79L76.8399 52.1L76.8299 52.09Z"
			fill="white"
		/>
	</svg>
);

type GroupedActivities = {
	key: string;
	label: string;
	items: Activity<ActivityType>[];
}[];

type ActivityFeedProps = {
	data: Activity<ActivityType>[];
};

export const ActivityFeed = ({ data: activities }: ActivityFeedProps) => {
	const { formatDate, formatMessage } = useIntl();

	const itemsByDate: GroupedActivities | null = useMemo(
		() =>
			activities.reduce<Array<{ key: string; label: string; items: Activity<ActivityType>[] }>>(
				(groups, item) => {
					const publishedDate = new Date(item.created);
					const key = formatInTimeZone(publishedDate, 'UTC', 'yyyy-MM-dd');
					const lastGroup = groups[groups.length - 1];

					if (lastGroup?.key === key) {
						lastGroup.items.push(item);
					} else {
						let label;
						if (isToday(publishedDate)) {
							label = formatMessage(messages.todayLabel);
						} else if (isYesterday(publishedDate)) {
							label = formatMessage(messages.yesterdayLabel);
						} else {
							label = formatDate(key, {
								weekday: 'long',
								year: 'numeric',
								month: 'long',
								day: 'numeric',
							});
						}

						groups.push({ key, label, items: [item] });
					}

					return groups;
				},
				[],
			),
		[activities, formatDate, formatMessage],
	);

	if (!activities.length) {
		return (
			<EmptyState
				icon={emptyIcon}
				headingText={formatMessage(messages.emptyStateTitle)}
				descriptionText={formatMessage(messages.emptyStateDescription)}
			/>
		);
	}

	return (
		<ChartWithHeaderContainer
			header={
				<>
					<Heading size="small" as="h2">
						{formatMessage(messages.title)}
					</Heading>
					<Text>{formatMessage(messages.description)}</Text>
				</>
			}
			chart={
				<Box xcss={containerStyles}>
					{itemsByDate.map(({ key, label, items }) => (
						<Fragment key={key}>
							<Heading size="xxsmall" as="h3">
								{label}
							</Heading>
							<Flex as="ul" direction="column" rowGap="space.200" xcss={activityListStyles}>
								{items.map((item) => (
									<JSErrorBoundary
										key={item.created}
										id="activityFeedItem"
										packageName="jiraContextualSummary"
										teamName="helios-reports"
									>
										<Box as="li">
											<ActivityItem activity={item} />
										</Box>
									</JSErrorBoundary>
								))}
							</Flex>
						</Fragment>
					))}
				</Box>
			}
		/>
	);
};

const containerStyles = xcss({
	overflowY: 'auto',
	overflowX: 'hidden',
	flexGrow: '1',
	marginRight: 'space.negative.150',
	paddingRight: 'space.150',
});

const activityListStyles = xcss({
	listStyleType: 'none',
	padding: '0',
	marginTop: 'space.100',
	marginBottom: 'space.100',
	paddingRight: 'space.050',
});
